<template>
  <!-- Боковая панель блога: поиск, фильтры (только в ленте), теги, популярное -->
  <aside class="flex flex-col gap-4">
    <section class="blog-panel">
      <p class="blog-panel__title">Поиск</p>

      <div class="flex flex-wrap gap-2">
        <button
          v-for="option in SEARCH_MODES"
          :key="option.value"
          type="button"
          class="flex items-center gap-2 text-[13px] leading-[normal] text-txmain transition-colors hover:text-[#2C6BFF]"
          @click="searchInDraft = option.value"
        >
          <span
            class="flex size-3.5 shrink-0 items-center justify-center rounded-full border transition-colors"
            :class="searchInDraft === option.value ? 'border-[#2C6BFF]' : 'border-[#CBD8EC]'"
          >
            <span
              class="size-1.5 rounded-full bg-[#2C6BFF] transition-opacity"
              :class="searchInDraft === option.value ? 'opacity-100' : 'opacity-0'"
            />
          </span>
          {{ option.label }}
        </button>
      </div>

      <form
        class="flex h-11 items-center gap-2 rounded-xl border border-[#E5EBF5] bg-white px-3 transition-colors focus-within:border-[#2C6BFF]"
        @submit.prevent="submitSearch"
      >
        <Icon name="lucide:search" size="18" class="block shrink-0 text-txopacity" />
        <input
          v-model="searchDraft"
          type="text"
          placeholder="Введите запрос..."
          class="min-w-0 flex-1 border-0 bg-transparent p-0 text-sm text-txmain outline-none placeholder:text-txopacity focus:ring-0"
        />
      </form>
    </section>

    <section v-if="withFilters" class="blog-panel">
      <p class="blog-panel__title">Фильтрация по:</p>

      <div class="flex flex-col gap-2">
        <el-select
          :model-value="topicSlug || undefined"
          class="blog-select"
          placeholder="Теме"
          popper-class="blog-select-popper"
          :offset="4"
          clearable
          filterable
          no-data-text="Ничего не найдено"
          no-match-text="Ничего не найдено"
          @change="setFilter('topicSlug', $event)"
        >
          <el-option
            v-for="topic in topics"
            :key="topic.id"
            :label="topic.name"
            :value="topic.slug"
          />
        </el-select>

        <el-select
          :model-value="authorSlug || undefined"
          class="blog-select"
          placeholder="Автору"
          popper-class="blog-select-popper"
          :offset="4"
          clearable
          filterable
          no-data-text="Ничего не найдено"
          no-match-text="Ничего не найдено"
          @change="setFilter('authorSlug', $event)"
        >
          <el-option
            v-for="author in authors"
            :key="author.id"
            :label="author.name"
            :value="author.slug"
          />
        </el-select>
      </div>
    </section>

    <section v-if="tags.length" class="blog-panel">
      <p class="blog-panel__title">Теги</p>

      <div class="flex flex-wrap gap-2">
        <!-- Повторный клик по выбранному тегу снимает фильтр: ведём на /blog,
             а не на страницу того же тега -->
        <NuxtLink
          v-for="tag in visibleTags"
          :key="tag.id"
          :to="tag.slug === activeTagSlug ? '/blog' : blogTagUrl(tag.slug)"
          :aria-pressed="tag.slug === activeTagSlug"
          :title="tag.slug === activeTagSlug ? `Убрать тег «${tag.name}»` : tag.name"
          class="flex w-fit items-center gap-1 rounded-md px-2 py-1.5 text-sm font-medium leading-[normal] transition-colors"
          :class="
            tag.slug === activeTagSlug
              ? 'bg-[#2C6BFF] text-white hover:bg-[#1E58E0]'
              : 'bg-[#EEF3FD] text-[#2C6BFF] hover:bg-[#DEE9FF]'
          "
        >
          {{ tag.name }}
          <Icon
            v-if="tag.slug === activeTagSlug"
            name="lucide:x"
            size="12"
            class="block shrink-0 text-white"
          />
        </NuxtLink>
      </div>

      <button
        v-if="tags.length > TAGS_LIMIT"
        type="button"
        class="h-11 w-full rounded-xl border border-[#2C6BFF]/15 bg-white text-sm font-medium text-[#2C6BFF] transition-colors hover:bg-[#EEF3FD]"
        @click="allTagsShown = !allTagsShown"
      >
        {{ allTagsShown ? "Свернуть" : "Показать ещё" }}
      </button>
    </section>

    <section v-if="popular.length" class="blog-panel">
      <p class="blog-panel__title">Популярное</p>

      <!-- Свой контейнер: у списка карточек шаг 12, а не 16, как у панели -->
      <div class="flex flex-col gap-3">
        <NuxtLink
          v-for="item in popular"
          :key="item.id"
          :to="blogArticleUrl(item)"
          class="group flex w-full items-center gap-3 rounded-xl border border-[#E5EBF5] bg-white p-2 transition-colors hover:border-[#2C6BFF]/40"
        >
          <div
            class="flex size-14 shrink-0 items-center justify-center overflow-hidden rounded-lg bg-[#EEF3FD]"
          >
            <NuxtImg
              v-if="item.cover"
              :src="item.cover"
              :alt="item.title"
              loading="lazy"
              class="size-full object-cover"
            />
            <!-- Обложки может не быть — вместо пустого прямоугольника знак блога -->
            <Icon v-else name="lucide:newspaper" size="20" class="block text-[#2C6BFF]/25" />
          </div>

          <div class="flex min-w-0 flex-1 flex-col gap-2">
            <!-- Две строки резервируем всегда: в макете все карточки одной
                 высоты (82), а у нас заголовки бывают и в одну строку -->
            <p class="line-clamp-2 min-h-10 text-sm font-medium leading-[1.4] text-txmain transition-colors group-hover:text-[#2C6BFF]">
              {{ item.title }}
            </p>
            <div class="flex items-center justify-between gap-2">
              <span
                v-if="item.primaryTopic?.name"
                class="min-w-0 truncate rounded-md bg-[#EEF3FD] px-1.5 py-0.5 text-xs font-medium leading-[normal] text-[#2C6BFF]"
              >
                {{ item.primaryTopic.name }}
              </span>
              <!-- Размер и цвет дублируем на каждом узле: в app.vue объявлено
                   не-scoped `span span { font-size: 14px; color: #3a3d44 }`,
                   и оно бьёт вложенные элементы сильнее, чем наследование -->
              <!-- ml-auto, а не только justify-between: без темы плашки нет,
                   и дата прижималась бы к левому краю -->
              <span class="ml-auto flex shrink-0 items-center gap-2 whitespace-nowrap">
                <time
                  v-if="item.publishedAt"
                  :datetime="blogDateIso(item.publishedAt)"
                  class="text-xs font-normal leading-[normal] text-txopacity"
                >
                  {{ blogDateShort(item.publishedAt) }}
                </time>
                <span
                  v-if="item.views != null"
                  class="flex items-center gap-1 text-xs font-normal leading-[normal] text-txopacity"
                >
                  <Icon name="lucide:eye" size="12" class="block shrink-0 text-txopacity" />
                  {{ blogViews(item.views) }}
                </span>
              </span>
            </div>
          </div>
        </NuxtLink>
      </div>
    </section>
  </aside>
</template>

<script setup lang="ts">
  import type { BlogArticleCard, BlogAuthor, BlogTag, BlogTopic } from "~/interfaces/blog";
  import { blogArticleUrl, blogTagUrl } from "~/common/blogUrl";
  import { blogDateIso, blogDateShort, blogViews } from "~/common/blogFormat";

  const props = withDefaults(
    defineProps<{
      topics?: BlogTopic[];
      tags?: BlogTag[];
      authors?: BlogAuthor[];
      popular?: BlogArticleCard[];
      // Блок фильтров есть только в ленте: на странице статьи его в макете нет
      withFilters?: boolean;
      search?: string;
      searchIn?: string;
      topicSlug?: string;
      authorSlug?: string;
      activeTagSlug?: string;
    }>(),
    { topics: () => [], tags: () => [], authors: () => [], popular: () => [] }
  );

  const emit = defineEmits<{
    (e: "search", payload: { search: string; searchIn: string }): void;
    (e: "filter", payload: { topicSlug?: string; authorSlug?: string }): void;
  }>();

  const SEARCH_MODES = [
    { value: "title", label: "по названию" },
    { value: "content", label: "по содержимому" },
  ];

  const TAGS_LIMIT = 10;

  const searchDraft = ref(props.search ?? "");
  const searchInDraft = ref(props.searchIn || "title");
  const allTagsShown = ref(false);

  watch(
    () => props.search,
    (value) => {
      searchDraft.value = value ?? "";
    }
  );

  const visibleTags = computed(() =>
    allTagsShown.value ? props.tags : props.tags.slice(0, TAGS_LIMIT)
  );

  const submitSearch = () => {
    emit("search", { search: searchDraft.value.trim(), searchIn: searchInDraft.value });
  };

  // Крестик очистки отдаёт пустую строку — приводим её к undefined, чтобы
  // лента сняла фильтр, а не искала по пустому слагу
  const setFilter = (key: "topicSlug" | "authorSlug", value?: string) => {
    emit("filter", { [key]: value || undefined });
  };
</script>

<style lang="scss" scoped>
  // Меню в лейауте объявляет не-scoped `a { width: 100% }` — теги и карточки
  // популярного из-за него встают столбиком
  a {
    width: auto;
  }

  .blog-panel {
    @apply flex flex-col gap-4 rounded-2xl border border-[#E5EBF5] bg-white p-5 max-lg:p-4;

    &__title {
      @apply w-full text-base font-medium leading-[normal] text-txmain;
    }
  }

  // FilterRow из макета: 36 в высоту, радиус 10, рамка #E5EBF5.
  // !important нужен из-за не-scoped `#__nuxt div.el-select__wrapper` в app.vue —
  // оно задаёт селектам свой радиус и цвет рамки и по специфичности сильнее
  .blog-select {
    :deep(.el-select__wrapper) {
      min-height: 36px;
      padding: 0 12px;
      border: 1px solid #e5ebf5 !important;
      border-radius: 10px !important;
      box-shadow: none !important;
      font-size: 14px;
      line-height: normal;
    }

    :deep(.el-select__wrapper.is-focused) {
      border-color: #2c6bff !important;
    }

    // В макете подпись поля тёмная, а не бледная, как по умолчанию у Element Plus
    :deep(.el-select__placeholder),
    :deep(.el-select__placeholder.is-transparent),
    :deep(.el-select__selected-item) {
      color: theme("colors.txmain");
      font-size: 14px;
      // Длинное название темы в узкой шторке иначе обрезается без многоточия
      text-overflow: ellipsis;
    }

    :deep(.el-select__caret) {
      color: theme("colors.txopacity");
      font-size: 16px;
    }
  }
</style>
